/**
 * RIBlockedDialog — surfaces the structured `impacts` payload that the
 * backend returns on a 409 ``ENTITY_HAS_DEPENDENCIES`` response.
 *
 * One row per ReferenceImpact: which entity type still references the
 * target, how many rows, and up to 5 sample ids. The user is told what
 * to clean up before retrying.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Archive, Loader2 } from 'lucide-react';
import type { ReferenceImpact } from '../../services/apiClient';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short headline ("Cannot delete activity", "Cannot save version config"). */
  title: string;
  /** Optional one-liner under the title — defaults to a generic explainer. */
  description?: string;
  impacts: ReferenceImpact[];
  /**
   * Optional fallback action. When provided, the dialog renders an
   * "Archive instead" button next to Close. Existing references stay
   * valid, but the entity is hidden from default lists and pickers
   * (so no new uses can wire to it). Restore is always available from
   * the archived view.
   *
   * The callback should perform the archive and resolve once the
   * underlying state has been updated; the dialog will close on
   * success and stay open on rejection.
   */
  onArchive?: () => Promise<void> | void;
}

const DEFAULT_DESC =
  "This action is blocked because other content still references it. Resolve the references below — or archive instead to keep the existing wiring intact while preventing new uses.";

function formatField(impact: ReferenceImpact): string {
  // For config-array shrinkage the "field" is the row's column (e.g.
  // variant_type) and reason carries the why; show both compactly.
  if (impact.reason) return `${impact.field} — ${impact.reason}`;
  return impact.field;
}

export function RIBlockedDialog({
  open,
  onOpenChange,
  title,
  description,
  impacts,
  onArchive,
}: Props) {
  const [isArchiving, setIsArchiving] = useState(false);

  const handleArchive = async () => {
    if (!onArchive || isArchiving) return;
    setIsArchiving(true);
    try {
      await onArchive();
      onOpenChange(false);
    } finally {
      setIsArchiving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-xl shadow-2xl max-w-xl border-brand-steel/50">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900 inline-flex items-center gap-2">
            <AlertTriangle size={16} className="text-brand-active" aria-hidden="true" />
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-brand-steel">
            {description ?? DEFAULT_DESC}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-brand-steel/50 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-brand-panel">
              <tr className="text-xs font-semibold text-brand-steel/70 uppercase tracking-wider">
                <th scope="col" className="px-3 py-2 text-left">Referenced by</th>
                <th scope="col" className="px-3 py-2 text-left">Field</th>
                <th scope="col" className="px-3 py-2 text-right">Count</th>
                <th scope="col" className="px-3 py-2 text-left">Sample IDs</th>
              </tr>
            </thead>
            <tbody>
              {impacts.map((impact, idx) => (
                <tr
                  key={`${impact.entity_type}-${impact.field}-${idx}`}
                  className="border-t border-brand-steel/50"
                >
                  <td className="px-3 py-2 text-gray-900">{impact.entity_type}</td>
                  <td className="px-3 py-2 text-brand-steel font-mono text-xs">{formatField(impact)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-gray-900">
                    {impact.count}
                  </td>
                  <td className="px-3 py-2 text-brand-steel/80 font-mono text-xs">
                    {impact.sample_ids.length > 0
                      ? impact.sample_ids.join(', ') + (impact.count > impact.sample_ids.length ? ', …' : '')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onArchive && (
            <Button
              type="button"
              onClick={handleArchive}
              disabled={isArchiving}
              className="inline-flex items-center gap-2"
            >
              {isArchiving ? (
                <Loader2 size={16} className="animate-spin" aria-hidden="true" />
              ) : (
                <Archive size={16} aria-hidden="true" />
              )}
              Archive instead
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RIBlockedDialog;
